import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import BusinessIcon from "@mui/icons-material/Business";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import TuneIcon from "@mui/icons-material/Tune";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import HistoryIcon from "@mui/icons-material/History";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import type { AdminActor } from "../types";

const drawerWidth = 250;
const items = [
  ["Overview", "/", DashboardIcon],
  ["Clients", "/clients", BusinessIcon],
  ["Scans", "/scans", DocumentScannerIcon],
  ["Alerts", "/alerts", NotificationsActiveIcon],
  ["Cleanup", "/cleanup", DeleteSweepIcon],
  ["Settings", "/settings", TuneIcon],
  ["Azure pool", "/azure-pool", CloudQueueIcon],
  ["Audit trail", "/audit", HistoryIcon],
  ["Administrators", "/administrators", AdminPanelSettingsIcon],
] as const;

export function AppLayout({ admin }: { admin: AdminActor }) {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up("md"));
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "#102d32",
        color: "white",
      }}
    >
      <Stack direction="row" gap={1.4} alignItems="center" px={2.5} py={2.6}>
        <Avatar sx={{ bgcolor: "#70d3c9", color: "#102d32", fontWeight: 900 }}>
          O
        </Avatar>
        <Box>
          <Typography fontWeight={850}>OSPREY</Typography>
          <Typography variant="caption" sx={{ color: "#9bb8bc" }}>
            OCR CONTROL
          </Typography>
        </Box>
      </Stack>
      <Divider sx={{ borderColor: "rgba(255,255,255,.09)" }} />
      <List sx={{ px: 1.2, py: 2, flex: 1 }}>
        {items.map(([label, to, Icon]) => (
          <ListItemButton
            key={to}
            component={NavLink}
            to={to}
            selected={
              location.pathname === to ||
              (to !== "/" && location.pathname.startsWith(to))
            }
            onClick={() => setOpen(false)}
            sx={{
              borderRadius: 2,
              mb: 0.5,
              color: "#c8d9dc",
              "&.Mui-selected": {
                bgcolor: "rgba(112,211,201,.14)",
                color: "#89e3d9",
              },
              "&.Mui-selected:hover": { bgcolor: "rgba(112,211,201,.2)" },
            }}
          >
            <ListItemIcon sx={{ color: "inherit", minWidth: 40 }}>
              <Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={label}
              primaryTypographyProps={{ fontWeight: 650, fontSize: 14 }}
            />
          </ListItemButton>
        ))}
      </List>
      <Box px={2.5} py={2}>
        <Typography variant="caption" sx={{ color: "#78979b" }}>
          Signed in as
        </Typography>
        <Typography
          variant="body2"
          className="truncate"
          sx={{ color: "#d9e7e9" }}
        >
          {admin.email}
        </Typography>
        <Typography variant="caption" sx={{ color: "#70d3c9" }}>
          {admin.role}
        </Typography>
      </Box>
    </Box>
  );
  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{ display: { md: "none" }, borderBottom: "1px solid #e7edef" }}
      >
        <Toolbar>
          <IconButton onClick={() => setOpen(true)}>
            <MenuIcon />
          </IconButton>
          <Typography fontWeight={800}>Osprey OCR Control</Typography>
        </Toolbar>
      </AppBar>
      {desktop ? (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": { width: drawerWidth, border: 0 },
          }}
        >
          {drawer}
        </Drawer>
      ) : (
        <Drawer
          open={open}
          onClose={() => setOpen(false)}
          sx={{ "& .MuiDrawer-paper": { width: drawerWidth } }}
        >
          {drawer}
        </Drawer>
      )}
      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          p: { xs: 2, sm: 3, lg: 4 },
          pt: { xs: 10, md: 4 },
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
